import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { Section, Subsection, Role, CreateSectionRequest, UpdateSectionRequest, CreateSubsectionRequest, UpdateSubsectionRequest } from '../../services/sectionsApi';

// State interfaces
export interface SectionsState {
  // Data
  sections: Section[];
  selectedSection: Section | null;
  roles: Role[];
  subsections: Subsection[];
  
  // UI State
  loading: boolean;
  error: string | null;
  
  // Form State
  isEditing: boolean;
  isDialogOpen: boolean;
  validationErrors: string[];
  
  // Subsection Management
  isSubsectionDialogOpen: boolean;
  selectedSubsection: Subsection | null;
  isEditingSubsection: boolean;
  subsectionValidationErrors: string[];
  
  // List State
  searchTerm: string;
  filters: {
    active: string; // 'all', 'true', 'false'
    roleId: number | null;
  };
  pagination: {
    page: number;
    pageSize: number;
    total: number;
  };
  
  // Statistics
  statistics: {
    total: number;
    active: number;
    inactive: number;
  };
  
  // Bulk Operations
  selectedSectionIds: number[];
  bulkLoading: boolean;
}

const initialState: SectionsState = {
  sections: [],
  selectedSection: null,
  roles: [],
  subsections: [],
  loading: false,
  error: null,
  isEditing: false,
  isDialogOpen: false,
  validationErrors: [],
  isSubsectionDialogOpen: false,
  selectedSubsection: null,
  isEditingSubsection: false,
  subsectionValidationErrors: [],
  searchTerm: '',
  filters: {
    active: 'all',
    roleId: null,
  },
  pagination: {
    page: 1,
    pageSize: 25,
    total: 0,
  },
  statistics: {
    total: 0,
    active: 0,
    inactive: 0,
  },
  selectedSectionIds: [],
  bulkLoading: false,
};

const sectionsSlice = createSlice({
  name: 'sections',
  initialState,
  reducers: {
    // Loading states
    setLoading: (state, action: PayloadAction<boolean>) => {
      state.loading = action.payload;
    },
    setBulkLoading: (state, action: PayloadAction<boolean>) => {
      state.bulkLoading = action.payload;
    },
    setError: (state, action: PayloadAction<string | null>) => {
      state.error = action.payload;
    },
    
    // Data actions
    setSections: (state, action: PayloadAction<Section[]>) => {
      state.sections = action.payload;
      state.pagination.total = action.payload.length;
    },
    setSelectedSection: (state, action: PayloadAction<Section | null>) => {
      state.selectedSection = action.payload;
    },
    setRoles: (state, action: PayloadAction<Role[]>) => {
      state.roles = action.payload;
    },
    setSubsections: (state, action: PayloadAction<Subsection[]>) => {
      state.subsections = action.payload;
    },
    addSection: (state, action: PayloadAction<Section>) => {
      state.sections.push(action.payload);
      state.pagination.total += 1;
    },
    updateSection: (state, action: PayloadAction<Section>) => {
      const index = state.sections.findIndex(section => section.Id === action.payload.Id);
      if (index !== -1) {
        state.sections[index] = action.payload;
      }
    },
    removeSection: (state, action: PayloadAction<number>) => {
      state.sections = state.sections.filter(section => section.Id !== action.payload);
      state.pagination.total -= 1;
    },
    
    // Subsection management
    addSubsection: (state, action: PayloadAction<Subsection>) => {
      state.subsections.push(action.payload);
      // Also update the selected section's subsections if applicable
      if (state.selectedSection && state.selectedSection.Id === action.payload.SectionId) {
        if (!state.selectedSection.Subsections) {
          state.selectedSection.Subsections = [];
        }
        state.selectedSection.Subsections.push(action.payload);
      }
    },
    updateSubsection: (state, action: PayloadAction<Subsection>) => {
      const index = state.subsections.findIndex(sub => sub.Id === action.payload.Id);
      if (index !== -1) {
        state.subsections[index] = action.payload;
      }
      // Also update in selected section if applicable
      if (state.selectedSection?.Subsections) {
        const subIndex = state.selectedSection.Subsections.findIndex(sub => sub.Id === action.payload.Id);
        if (subIndex !== -1) {
          state.selectedSection.Subsections[subIndex] = action.payload;
        }
      }
    },
    removeSubsection: (state, action: PayloadAction<number>) => {
      state.subsections = state.subsections.filter(sub => sub.Id !== action.payload);
      // Also remove from selected section if applicable
      if (state.selectedSection?.Subsections) {
        state.selectedSection.Subsections = state.selectedSection.Subsections.filter(sub => sub.Id !== action.payload);
      }
    },
    
    // Dialog and form actions
    setDialogOpen: (state, action: PayloadAction<boolean>) => {
      state.isDialogOpen = action.payload;
      if (!action.payload) {
        state.selectedSection = null;
        state.isEditing = false;
        state.validationErrors = [];
      }
    },
    setEditing: (state, action: PayloadAction<boolean>) => {
      state.isEditing = action.payload;
    },
    setValidationErrors: (state, action: PayloadAction<string[]>) => {
      state.validationErrors = action.payload;
    },
    
    // Subsection dialog actions
    setSubsectionDialogOpen: (state, action: PayloadAction<boolean>) => {
      state.isSubsectionDialogOpen = action.payload;
      if (!action.payload) {
        state.selectedSubsection = null;
        state.isEditingSubsection = false;
        state.subsectionValidationErrors = [];
      }
    },
    setSelectedSubsection: (state, action: PayloadAction<Subsection | null>) => {
      state.selectedSubsection = action.payload;
    },
    setEditingSubsection: (state, action: PayloadAction<boolean>) => {
      state.isEditingSubsection = action.payload;
    },
    setSubsectionValidationErrors: (state, action: PayloadAction<string[]>) => {
      state.subsectionValidationErrors = action.payload;
    },
    
    // Search and filters
    setSearchTerm: (state, action: PayloadAction<string>) => {
      state.searchTerm = action.payload;
      state.pagination.page = 1; // Reset to first page when searching
    },
    setFilters: (state, action: PayloadAction<Partial<SectionsState['filters']>>) => {
      state.filters = { ...state.filters, ...action.payload };
      state.pagination.page = 1; // Reset to first page when filtering
    },
    
    // Pagination
    setPagination: (state, action: PayloadAction<Partial<SectionsState['pagination']>>) => {
      state.pagination = { ...state.pagination, ...action.payload };
    },
    
    // Statistics
    setStatistics: (state, action: PayloadAction<SectionsState['statistics']>) => {
      state.statistics = action.payload;
    },
    
    // Bulk operations
    setSelectedSectionIds: (state, action: PayloadAction<number[]>) => {
      state.selectedSectionIds = action.payload;
    },
    toggleSectionSelection: (state, action: PayloadAction<number>) => {
      const sectionId = action.payload;
      const isSelected = state.selectedSectionIds.includes(sectionId);
      if (isSelected) {
        state.selectedSectionIds = state.selectedSectionIds.filter(id => id !== sectionId);
      } else {
        state.selectedSectionIds.push(sectionId);
      }
    },
    selectAllSections: (state) => {
      state.selectedSectionIds = state.sections.map(section => section.Id);
    },
    deselectAllSections: (state) => {
      state.selectedSectionIds = [];
    },
    
    // Reset state
    resetState: () => initialState,
  },
});

// Action creators
export const {
  setLoading,
  setBulkLoading,
  setError,
  setSections,
  setSelectedSection,
  setRoles,
  setSubsections,
  addSection,
  updateSection,
  removeSection,
  addSubsection,
  updateSubsection,
  removeSubsection,
  setDialogOpen,
  setEditing,
  setValidationErrors,
  setSubsectionDialogOpen,
  setSelectedSubsection,
  setEditingSubsection,
  setSubsectionValidationErrors,
  setSearchTerm,
  setFilters,
  setPagination,
  setStatistics,
  setSelectedSectionIds,
  toggleSectionSelection,
  selectAllSections,
  deselectAllSections,
  resetState,
} = sectionsSlice.actions;

// Saga action types
export const FETCH_SECTIONS = 'sections/fetchSections';
export const FETCH_SECTION_BY_ID = 'sections/fetchSectionById';
export const CREATE_SECTION = 'sections/createSection';
export const UPDATE_SECTION = 'sections/updateSection';
export const DELETE_SECTION = 'sections/deleteSection';
export const FETCH_ROLES = 'sections/fetchRoles';
export const CREATE_SUBSECTION = 'sections/createSubsection';
export const UPDATE_SUBSECTION = 'sections/updateSubsection';
export const DELETE_SUBSECTION = 'sections/deleteSubsection';
export const FETCH_SUBSECTIONS = 'sections/fetchSubsections';
export const BULK_ACTIVATE_SECTIONS = 'sections/bulkActivateSections';
export const BULK_DEACTIVATE_SECTIONS = 'sections/bulkDeactivateSections';
export const FETCH_SECTIONS_STATISTICS = 'sections/fetchSectionsStatistics';

// Saga action creators
export const fetchSections = () => ({ type: FETCH_SECTIONS });
export const fetchSectionById = (id: number) => ({ type: FETCH_SECTION_BY_ID, payload: id });
export const createSection = (section: CreateSectionRequest) => ({ type: CREATE_SECTION, payload: section });
export const updateSectionAction = (section: UpdateSectionRequest) => ({ type: UPDATE_SECTION, payload: section });
export const deleteSection = (id: number) => ({ type: DELETE_SECTION, payload: id });
export const fetchRoles = () => ({ type: FETCH_ROLES });
export const createSubsection = (subsection: CreateSubsectionRequest) => ({ type: CREATE_SUBSECTION, payload: subsection });
export const updateSubsectionAction = (subsection: UpdateSubsectionRequest) => ({ type: UPDATE_SUBSECTION, payload: subsection });
export const deleteSubsection = (id: number) => ({ type: DELETE_SUBSECTION, payload: id });
export const fetchSubsections = (sectionId: number) => ({ type: FETCH_SUBSECTIONS, payload: sectionId });
export const bulkActivateSections = (ids: number[]) => ({ type: BULK_ACTIVATE_SECTIONS, payload: ids });
export const bulkDeactivateSections = (ids: number[]) => ({ type: BULK_DEACTIVATE_SECTIONS, payload: ids });
export const fetchSectionsStatistics = () => ({ type: FETCH_SECTIONS_STATISTICS });

export default sectionsSlice.reducer; 